/**
 * Responsibility: describe each building archetype as a stack of horizontal rings.
 *
 * ## Why rings, and not just lines
 *
 * The first version of this module returned line segments directly. That worked until the shapes needed
 * **depth masks**: a spire masked as a box occludes the empty air beside it, and a box mask across the
 * twin towers fills in the gap that is the entire point of that shape. Masks have to match the drawn
 * silhouette — the same lesson the tree masks taught in Task 7.5.
 *
 * So an archetype instead declares its *solids*: one or more stacks of rings, bottom to top. Everything
 * else is derived from that single description —
 *
 * - **lines** are each ring plus the verticals joining consecutive rings corner to corner
 * - **the mask** is the quads between consecutive rings, plus a cap
 *
 * which means a mask can never disagree with the shape it is masking. A twisted tower's helical
 * verticals and its twisting mask come from the same rotated rings; a setback's shelf is two rings at
 * the same height with different widths, and reads correctly as both a line and a surface.
 *
 * `extras` carries the lines that are decoration rather than structure: floor bands, bracing diagonals,
 * a parapet, a sky bridge. They are drawn and never masked.
 *
 * ## Silhouette is the whole point
 *
 * At chase-camera distance outline is all that reads, established when the aircraft models were built.
 * Every archetype here is chosen because its *profile* is unmistakable, and each comes from real
 * architecture. `debug/buildings.html` exists to check that claim honestly.
 *
 * Contains no Three.js and no DOM, so it is fully unit testable.
 */

import {
  countSolidsMask,
  fractionAt,
  groundRing,
  linesForSolid,
  polygonFootprint,
  rectFootprint,
  ringAt,
  samePoint,
  segmentAt as segment,
} from './ringStack.js';
import { BUILDING } from '../config.js';

/** Degrees to radians. Shape parameters are authored in degrees because humans read those. */
const DEG_TO_RAD = Math.PI / 180;

/** Normalise a spec into the values every shape needs. */
function resolve(spec) {
  const baseY = spec.baseY ?? 0;
  return {
    centerX: spec.centerX ?? 0,
    centerZ: spec.centerZ ?? 0,
    width: spec.width,
    depth: spec.depth ?? spec.width,
    baseY,
    height: spec.height,
    roofY: baseY + spec.height,
    groundAt: spec.sampleBaseY ?? (() => baseY),
  };
}

// ---------------------------------------------------------------------------
// The archetypes
// ---------------------------------------------------------------------------

/** A plain rectangular tower. The existing city building. */
function boxSolids(spec) {
  const { centerX, centerZ, width, depth, roofY, groundAt } = resolve(spec);
  const footprint = rectFootprint(centerX, centerZ, width, depth);
  return [{ rings: [groundRing(footprint, groundAt), ringAt(footprint, roofY)] }];
}

/** A parapet: an inset ring on the roof, so a low block still has something to look at. */
function podiumExtras(spec) {
  const { centerX, centerZ, width, depth, baseY, height, roofY } = resolve(spec);
  const inset = 1 - BUILDING.PODIUM.ROOF_BAND_INSET * 2;
  const ring = ringAt(rectFootprint(centerX, centerZ, width * inset, depth * inset), roofY);
  return linesForSolid({ rings: [ring] }, baseY, height);
}

/** Stepped wedding-cake mass. Empire State. */
function setbackSolids(spec) {
  const { centerX, centerZ, width, depth, baseY, height, groundAt } = resolve(spec);
  const { TIERS, SHRINK_PER_TIER } = BUILDING.SETBACK;

  const rings = [];

  for (let tier = 0; tier < TIERS; tier += 1) {
    const scale = Math.max(0.1, 1 - tier * SHRINK_PER_TIER);
    const footprint = rectFootprint(centerX, centerZ, width * scale, depth * scale);
    const bottomY = baseY + (height * tier) / TIERS;
    const topY = baseY + (height * (tier + 1)) / TIERS;

    /*
      Two rings per tier at the same width. The pair at a shared height between tiers is the setback
      shelf itself — as lines it is the step outline, and as mask surface it is the horizontal ledge.
    */
    rings.push(tier === 0 ? groundRing(footprint, groundAt) : ringAt(footprint, bottomY));
    rings.push(ringAt(footprint, topY));
  }

  return [{ rings }];
}

/** A frustum, wider at the base. Shared by the taper and the spire. */
function taperedSolids(spec, topScale) {
  const { centerX, centerZ, width, depth, roofY, groundAt } = resolve(spec);
  const base = rectFootprint(centerX, centerZ, width, depth);
  const top = rectFootprint(centerX, centerZ, width * topScale, depth * topScale);
  return [{ rings: [groundRing(base, groundAt), ringAt(top, roofY)] }];
}

/**
 * How much a twisted tower must shrink to stay inside its own footprint.
 *
 * Rotating a rectangle grows its bounding box — a square turned 45 degrees is √2 times wider. Left
 * uncorrected a twisted tower would overhang its block, and **the city's guarantee that two buildings
 * never intersect comes from footprint plus street width rather than from collision checks.** One shape
 * quietly breaking that would reintroduce overlaps with nothing to catch them.
 */
function twistFootprintScale(width, depth, levels, radiansPerLevel) {
  let worst = 1;

  for (let level = 0; level < levels; level += 1) {
    const angle = level * radiansPerLevel;
    const cos = Math.abs(Math.cos(angle));
    const sin = Math.abs(Math.sin(angle));

    worst = Math.max(
      worst,
      ((width / 2) * cos + (depth / 2) * sin) / (width / 2),
      ((width / 2) * sin + (depth / 2) * cos) / (depth / 2),
    );
  }

  return 1 / worst;
}

/** Each level rotated against the one below, so the verticals become helices. Turning Torso. */
function twistSolids(spec) {
  const { centerX, centerZ, width, depth, baseY, height, groundAt } = resolve(spec);
  const { LEVELS, DEGREES_PER_LEVEL } = BUILDING.TWIST;
  const levels = Math.max(2, LEVELS);
  const radiansPerLevel = DEGREES_PER_LEVEL * DEG_TO_RAD;

  const scale = twistFootprintScale(width, depth, levels, radiansPerLevel);
  const footprintAt = (level) =>
    rectFootprint(centerX, centerZ, width * scale, depth * scale, level * radiansPerLevel);

  const rings = [groundRing(footprintAt(0), groundAt)];
  for (let level = 1; level < levels; level += 1) {
    rings.push(ringAt(footprintAt(level), baseY + (height * level) / (levels - 1)));
  }

  return [{ rings }];
}

/** A round footprint among rectangular ones. Marina City. */
function cylinderSolids(spec) {
  const { centerX, centerZ, width, depth, roofY, groundAt } = resolve(spec);
  const footprint = polygonFootprint(centerX, centerZ, width / 2, depth / 2, BUILDING.CYLINDER.SIDES);
  return [{ rings: [groundRing(footprint, groundAt), ringAt(footprint, roofY)] }];
}

/**
 * A broad thin plate. Unité d'Habitation.
 *
 * Depth is overridden here rather than left to the caller, so the archetype guarantees its own
 * thinness — a "slab" placed in a square block would otherwise just be a box.
 */
function slabFootprint(spec) {
  const { centerX, centerZ, width, depth } = resolve(spec);
  return rectFootprint(centerX, centerZ, width, depth * BUILDING.SLAB.DEPTH_SCALE);
}

function slabSolids(spec) {
  const { roofY, groundAt } = resolve(spec);
  const footprint = slabFootprint(spec);
  return [{ rings: [groundRing(footprint, groundAt), ringAt(footprint, roofY)] }];
}

/** Floor bands. A bare slab has no scale cue at all; these supply it. */
function slabExtras(spec) {
  const { baseY, height } = resolve(spec);
  const footprint = slabFootprint(spec);
  const rings = [];

  for (let band = 1; band <= BUILDING.SLAB.BANDS; band += 1) {
    rings.push(ringAt(footprint, baseY + (height * band) / (BUILDING.SLAB.BANDS + 1)));
  }

  return rings.flatMap((ring) => linesForSolid({ rings: [ring] }, baseY, height));
}

/** Two towers joined by a sky bridge. Petronas. */
function twinGeometry(spec) {
  const resolved = resolve(spec);
  const { GAP_FRACTION } = BUILDING.TWIN;
  const gap = resolved.width * GAP_FRACTION;
  const towerWidth = (resolved.width - gap) / 2;
  const offset = (towerWidth + gap) / 2;
  return { ...resolved, gap, towerWidth, offset };
}

function twinSolids(spec) {
  const { centerX, centerZ, depth, roofY, groundAt, towerWidth, offset } = twinGeometry(spec);

  return [centerX - offset, centerX + offset].map((towerX) => {
    const footprint = rectFootprint(towerX, centerZ, towerWidth, depth);
    return { rings: [groundRing(footprint, groundAt), ringAt(footprint, roofY)] };
  });
}

/**
 * The sky bridge.
 *
 * Drawn as a thin box rather than a single line — a horizontal between two verticals is the whole reason
 * this archetype exists, and a bare wire would read as an artefact. Deliberately an extra rather than a
 * solid: masking it would fill part of the gap, and the gap is the shape.
 */
function twinExtras(spec) {
  const { centerX, centerZ, depth, baseY, height, gap } = twinGeometry(spec);
  const { BRIDGE_HEIGHT_FRACTION, BRIDGE_THICKNESS_FRACTION } = BUILDING.TWIN;

  const bridgeY = baseY + height * BRIDGE_HEIGHT_FRACTION;
  const topY = bridgeY + height * BRIDGE_THICKNESS_FRACTION;
  const left = centerX - gap / 2;
  const right = centerX + gap / 2;
  const t = (y) => fractionAt(y, baseY, height);

  const out = [];
  for (const z of [centerZ - depth / 2, centerZ + depth / 2]) {
    out.push(segment(left, bridgeY, z, right, bridgeY, z, t(bridgeY), t(bridgeY)));
    out.push(segment(left, topY, z, right, topY, z, t(topY), t(topY)));
  }

  return out;
}

/** An open braced frame. Eiffel, or a transmission pylon. */
function latticeRings(spec) {
  const { centerX, centerZ, width, depth, baseY, height, groundAt } = resolve(spec);
  const { TIERS, TOP_SCALE } = BUILDING.LATTICE;

  const footprintAt = (tier) => {
    const scale = 1 - (1 - TOP_SCALE) * (tier / TIERS);
    return rectFootprint(centerX, centerZ, width * scale, depth * scale);
  };

  const rings = [groundRing(footprintAt(0), groundAt)];
  for (let tier = 1; tier <= TIERS; tier += 1) {
    rings.push(ringAt(footprintAt(tier), baseY + (height * tier) / TIERS));
  }

  return rings;
}

function latticeSolids(spec) {
  return [{ rings: latticeRings(spec) }];
}

/** Bracing. One diagonal per face per tier, which is what makes it read as braced. */
function latticeExtras(spec) {
  const { baseY, height } = resolve(spec);
  const rings = latticeRings(spec);
  const out = [];

  for (let r = 0; r < rings.length - 1; r += 1) {
    const lower = rings[r].points;
    const upper = rings[r + 1].points;

    for (let i = 0; i < lower.length; i += 1) {
      const a = lower[i];
      const b = upper[(i + 1) % upper.length];
      out.push(
        segment(a[0], a[1], a[2], b[0], b[1], b[2], fractionAt(a[1], baseY, height), fractionAt(b[1], baseY, height)),
      );
    }
  }

  return out;
}

/** A low dome. Horizontal emphasis, to punctuate a skyline of verticals. */
function domeSolids(spec) {
  const { centerX, centerZ, width, depth, baseY, height, roofY, groundAt } = resolve(spec);
  const { SIDES, LATITUDE_RINGS } = BUILDING.DOME;

  /*
    Rings follow a quarter circle, so the profile is a dome rather than a cone: at fraction f of the
    height the radius is cos(f * pi/2) of the base.
  */
  const footprintAt = (fraction) => {
    const shrink = Math.cos((fraction * Math.PI) / 2);
    return polygonFootprint(centerX, centerZ, (width / 2) * shrink, (depth / 2) * shrink, SIDES);
  };

  const rings = [groundRing(footprintAt(0), groundAt)];
  for (let i = 1; i <= LATITUDE_RINGS; i += 1) {
    const fraction = i / (LATITUDE_RINGS + 1);
    rings.push(ringAt(footprintAt(fraction), baseY + height * fraction));
  }

  return [{ rings, apex: [centerX, roofY, centerZ] }];
}

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

/**
 * Turn an archetype's solids and extras into line segments.
 *
 * The single place lines are derived, so no archetype can draw something its mask does not cover.
 */
function buildFrom(descriptor, spec) {
  const { baseY, height } = resolve(spec);
  const out = [];

  for (const solid of descriptor.solids(spec)) {
    out.push(...linesForSolid(solid, baseY, height));
  }

  if (descriptor.extras) out.push(...descriptor.extras(spec));

  return out;
}

/**
 * Every archetype, as a dispatch table matching `structureKinds.js`.
 *
 * `masked: false` means the shape casts no depth mask. The lattice is the only one: its wireframe *is*
 * the structure, so there is no solid surface for a mask to represent, and masking it would make an
 * open frame occlude like a slab.
 */
const DESCRIPTORS = [
  { id: 'box', label: 'Box tower', reference: 'the existing city building', solids: boxSolids },
  {
    id: 'podium',
    label: 'Low podium',
    reference: 'most of any real city',
    solids: boxSolids,
    extras: podiumExtras,
  },
  { id: 'setback', label: 'Setback ziggurat', reference: 'Empire State', solids: setbackSolids },
  {
    id: 'taper',
    label: 'Tapered tower',
    reference: 'Transamerica Pyramid',
    solids: (spec) => taperedSolids(spec, BUILDING.TAPER.TOP_SCALE),
  },
  {
    id: 'spire',
    label: 'Spire',
    reference: 'the Shard',
    solids: (spec) => taperedSolids(spec, BUILDING.SPIRE.TOP_SCALE),
  },
  { id: 'twist', label: 'Twisted tower', reference: 'Turning Torso', solids: twistSolids },
  { id: 'cylinder', label: 'Cylinder', reference: 'Marina City', solids: cylinderSolids },
  {
    id: 'slab',
    label: 'Slab',
    reference: "Unité d'Habitation",
    solids: slabSolids,
    extras: slabExtras,
  },
  {
    id: 'twin',
    label: 'Twin + sky bridge',
    reference: 'Petronas',
    solids: twinSolids,
    extras: twinExtras,
  },
  {
    id: 'lattice',
    label: 'Lattice tower',
    reference: 'Eiffel, transmission pylon',
    solids: latticeSolids,
    extras: latticeExtras,
    masked: false,
  },
  { id: 'dome', label: 'Dome / arena', reference: 'any stadium', solids: domeSolids },
];

/** The registry, each entry given a `build` and a resolved `masked` flag. */
export const BUILDING_SHAPES = DESCRIPTORS.map((descriptor) => ({
  ...descriptor,
  masked: descriptor.masked !== false,
  build: (spec) => buildFrom(descriptor, spec),
}));

/**
 * Look up an archetype by id, falling back to the plain box.
 *
 * Falls back rather than throwing, for the same reason the aircraft registry does: a saved or configured
 * id naming a shape that has since been renamed should degrade, not break a world.
 */
export function getBuildingShapeOrDefault(id) {
  return BUILDING_SHAPES.find((shape) => shape.id === id) ?? BUILDING_SHAPES[0];
}

/**
 * Segment count for an archetype at a given spec.
 *
 * Measured by building it rather than derived by formula. A second formula to keep in step with the
 * emitter is exactly the kind of duplication that goes quietly wrong — `segmentsForTree` needed a test
 * asserting it agreed with `emitTree` for this reason.
 */
export function countSegments(shape, spec) {
  return shape.build(spec).length;
}

/**
 * Mask vertex and triangle counts for an archetype at a given spec.
 *
 * Needed before allocating, because `tileContent` sizes one shared buffer for a whole tile. Counted from
 * the real ring stacks for the same reason as `countSegments`.
 *
 * Per solid with R rings of N points: the lateral surface is (R-1) bands of N quads, the top is a fan of
 * N triangles from an added centre vertex, and an apex replaces that fan with its own N triangles.
 *
 * @returns {{vertices: number, triangles: number}} Zeroes when the shape casts no mask.
 */
export function countMask(shape, spec) {
  if (!shape.masked) return { vertices: 0, triangles: 0 };
  return countSolidsMask(shape.solids(spec));
}

/** The ring stacks for a shape, for the mask emitter. */
export function solidsFor(shape, spec) {
  return shape.masked ? shape.solids(spec) : [];
}

/**
 * The horizontal positions where a shape meets the ground.
 *
 * Placement needs these so it can sample the terrain at the points the shape *actually* stands on. Four
 * rect corners are not enough once footprints can be rotated, polygonal, or split in two: a cylinder's
 * eight base points sit inside the rectangle, and guessing their height from the nearest corner put
 * vertices below the drawn surface — buildings visibly sinking into the ground.
 *
 * Returned as `[x, z]` pairs in ring order, which is stable for a given spec, so a caller can sample
 * ground once per point and hand the results back through `spec.sampleBaseY`.
 *
 * @param {object} shape - A registry entry.
 * @param {object} spec
 * @returns {Array<[number, number]>}
 */
export function baseFootprintPoints(shape, spec) {
  const points = [];

  // Ground height does not affect x or z, so a flat spec is enough to discover where the base sits.
  for (const solid of shape.solids({ ...spec, sampleBaseY: undefined })) {
    for (const [x, , z] of solid.rings[0].points) {
      points.push([x, z]);
    }
  }

  return points;
}
